import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Settings, 
  Mail, 
  Server, 
  School, 
  CreditCard, 
  Calendar,
  GraduationCap,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Edit,
  MapPin,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminUsersTab from './AdminUsersTab';

// Academic Year Manager Component
const AcademicYearManager = ({ promoteStudents, gradeSequence }) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newYear, setNewYear] = useState({
    year_name: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchAcademicYears();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get('/academic-years');
      setAcademicYears(response.data);
    } catch (error) {
      console.error('Error fetching academic years:', error);
    }
  };

  const handleCreateYear = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert dates to ISO format
      const payload = {
        year_name: newYear.year_name,
        start_date: new Date(newYear.start_date).toISOString(),
        end_date: new Date(newYear.end_date).toISOString()
      };

      await axios.post('/academic-years', payload);
      toast.success('Año académico creado exitosamente. Las fechas de las semanas 10, 20, 30 y 40 han sido calculadas automáticamente.');
      
      setShowCreateForm(false);
      setNewYear({ year_name: '', start_date: '', end_date: '' });
      fetchAcademicYears();
    } catch (error) {
      console.error('Error creating academic year:', error);
      toast.error('Error al crear año académico: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleActivateYear = async (yearId) => {
    try {
      await axios.put(`/academic-years/${yearId}/activate`);
      toast.success('Año académico activado exitosamente');
      fetchAcademicYears();
    } catch (error) {
      console.error('Error activating year:', error);
      toast.error('Error al activar año académico');
    }
  };

  const handleDeleteYear = async (yearId) => {
    if (!window.confirm('¿Estás seguro de eliminar este año académico?')) return;

    try {
      await axios.delete(`/academic-years/${yearId}`);
      toast.success('Año académico eliminado exitosamente');
      fetchAcademicYears();
    } catch (error) {
      console.error('Error deleting year:', error);
      toast.error('Error al eliminar año académico');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-6">
      <Card className="modern-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Gestión de Años Académicos
            </CardTitle>
            <Button 
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Año Académico
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Al crear un año académico, el sistema calcula automáticamente las fechas de las semanas 10, 20, 30 y 40 
              para el envío de reportes de calificaciones.
            </AlertDescription>
          </Alert>

          {/* Create Form */}
          {showCreateForm && (
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <form onSubmit={handleCreateYear} className="space-y-4">
                  <h3 className="font-semibold text-purple-900 mb-4">Crear Nuevo Año Académico</h3>
                  
                  <div>
                    <Label htmlFor="year_name">Nombre del Año Académico *</Label>
                    <Input
                      id="year_name"
                      placeholder="Ej: 2024-2025"
                      value={newYear.year_name}
                      onChange={(e) => setNewYear({...newYear, year_name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Fecha de Inicio *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={newYear.start_date}
                        onChange={(e) => setNewYear({...newYear, start_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">Fecha de Fin *</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={newYear.end_date}
                        onChange={(e) => setNewYear({...newYear, end_date: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                      {loading ? 'Creando...' : 'Crear Año Académico'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Academic Years List */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Años Académicos Registrados</h3>
            
            {academicYears.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No hay años académicos registrados</p>
                <p className="text-sm">Crea uno para comenzar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {academicYears.map((year) => (
                  <Card key={year.id} className={year.is_current ? 'border-purple-500 border-2' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-lg">{year.year_name}</h4>
                            {year.is_current && (
                              <Badge className="bg-purple-600">Año Activo</Badge>
                            )}
                            {!year.is_active && (
                              <Badge variant="outline">Inactivo</Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Inicio</p>
                              <p className="font-medium">{formatDate(year.start_date)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Fin</p>
                              <p className="font-medium">{formatDate(year.end_date)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Semana 10</p>
                              <p className="font-medium">{formatDate(year.week_10_date)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Semana 20</p>
                              <p className="font-medium">{formatDate(year.week_20_date)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Semana 30</p>
                              <p className="font-medium">{formatDate(year.week_30_date)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Semana 40</p>
                              <p className="font-medium">{formatDate(year.week_40_date)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          {!year.is_current && (
                            <Button
                              size="sm"
                              onClick={() => handleActivateYear(year.id)}
                              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                            >
                              Activar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteYear(year.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Student Promotion Section */}
          <Card className="bg-blue-50 border-blue-200 mt-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-blue-900">Promoción Manual de Estudiantes</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Promover todos los estudiantes activos al siguiente grado ahora
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <p className="text-xs text-blue-600">Secuencia: </p>
                    {(gradeSequence || []).map((grade, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {index + 1}. {grade}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button onClick={promoteStudents} className="bg-blue-600 hover:bg-blue-700">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Promover Estudiantes
                </Button>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};

const SettingsTab = ({ settings, updateSettings, promoteStudents, settingsLoading }) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [activeSection, setActiveSection] = useState('smtp');
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [emailToggleLoading, setEmailToggleLoading] = useState(false);

  useEffect(() => {
    const fetchEmailStatus = async () => {
      try {
        const res = await axios.get('/schools/my-school/email-notifications');
        setEmailNotificationsEnabled(res.data.email_notifications_enabled);
      } catch (e) { /* ignore */ }
    };
    fetchEmailStatus();
  }, []);

  const toggleEmailNotifications = async () => {
    setEmailToggleLoading(true);
    try {
      const res = await axios.put('/schools/my-school/email-notifications', {
        email_notifications_enabled: !emailNotificationsEnabled
      });
      setEmailNotificationsEnabled(res.data.email_notifications_enabled);
      toast.success(res.data.message);
    } catch (e) {
      toast.error('Error al cambiar notificaciones');
    }
    setEmailToggleLoading(false);
  };

  const handleInputChange = (field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    await updateSettings(localSettings);
  };

  const testSmtpConnection = async () => {
    try {
      // Save first to make sure backend has the latest values
      await updateSettings(localSettings);
      const res = await axios.post('/settings/test-smtp');
      toast.success(res.data.message || 'Correo de prueba enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al probar SMTP');
    }
  };

  return (
    <div className="space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Configuración del Sistema
        </h1>
        <p className="text-gray-600">
          Administra las configuraciones globales del sistema
        </p>
      </div>

      {/* Settings Navigation */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Button
          variant={activeSection === 'smtp' ? 'default' : 'outline'}
          onClick={() => setActiveSection('smtp')}
          className="flex items-center justify-center gap-2"
        >
          <Mail className="w-4 h-4" />
          SMTP
        </Button>
        <Button
          variant={activeSection === 'school' ? 'default' : 'outline'}
          onClick={() => setActiveSection('school')}
          className="flex items-center justify-center gap-2"
        >
          <School className="w-4 h-4" />
          Escuela
        </Button>
        <Button
          variant={activeSection === 'academic' ? 'default' : 'outline'}
          onClick={() => setActiveSection('academic')}
          className="flex items-center justify-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          Académico
        </Button>
        <Button
          variant={activeSection === 'payments' ? 'default' : 'outline'}
          onClick={() => setActiveSection('payments')}
          className="flex items-center justify-center gap-2"
        >
          <CreditCard className="w-4 h-4" />
          Pagos
        </Button>
        <Button
          variant={activeSection === 'location' ? 'default' : 'outline'}
          onClick={() => setActiveSection('location')}
          className="flex items-center justify-center gap-2"
        >
          <MapPin className="w-4 h-4" />
          Ubicación
        </Button>
        <Button
          variant={activeSection === 'users' ? 'default' : 'outline'}
          onClick={() => setActiveSection('users')}
          className="flex items-center justify-center gap-2"
          data-testid="settings-users-tab"
        >
          <Users className="w-4 h-4" />
          Usuarios
        </Button>
      </div>

      {/* Admin Users Section */}
      {activeSection === 'users' && (
        <Card className="modern-card">
          <CardContent className="pt-6">
            <AdminUsersTab />
          </CardContent>
        </Card>
      )}

      {/* SMTP Configuration */}
      {activeSection === 'smtp' && (
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Configuración SMTP
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Configure el servidor SMTP para enviar notificaciones automáticas por email a padres y profesores.
              </AlertDescription>
            </Alert>

            {/* Email Notifications Toggle */}
            <Card className={`border-2 ${emailNotificationsEnabled ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-sm text-slate-800">Notificaciones por Email</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      {emailNotificationsEnabled
                        ? 'Los emails automáticos a padres están activados'
                        : 'Los emails automáticos a padres están desactivados'}
                    </p>
                  </div>
                  <Button
                    onClick={toggleEmailNotifications}
                    disabled={emailToggleLoading}
                    variant={emailNotificationsEnabled ? 'default' : 'outline'}
                    className={emailNotificationsEnabled ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid="toggle-email-notifications"
                  >
                    {emailToggleLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : emailNotificationsEnabled ? (
                      <><CheckCircle className="w-4 h-4 mr-2" /> Activado</>
                    ) : (
                      <><AlertCircle className="w-4 h-4 mr-2" /> Desactivado</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="smtp_host">Servidor SMTP</Label>
                <Input
                  id="smtp_host"
                  value={localSettings.smtp_host || ''}
                  onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div>
                <Label htmlFor="smtp_port">Puerto</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={localSettings.smtp_port || 587}
                  onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value))}
                  placeholder="587"
                />
              </div>
              <div>
                <Label htmlFor="smtp_username">Usuario</Label>
                <Input
                  id="smtp_username"
                  value={localSettings.smtp_username || ''}
                  onChange={(e) => handleInputChange('smtp_username', e.target.value)}
                  placeholder="usuario@gmail.com"
                />
              </div>
              <div>
                <Label htmlFor="smtp_password">
                  Contraseña
                  {localSettings.has_smtp_password && (
                    <span className="ml-2 text-xs text-emerald-600 font-normal">
                      ✓ contraseña guardada — déjalo vacío para mantenerla
                    </span>
                  )}
                </Label>
                <Input
                  id="smtp_password"
                  type="password"
                  value={localSettings.smtp_password || ''}
                  onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                  placeholder="Contraseña o App Password"
                />
              </div>
              <div>
                <Label htmlFor="smtp_from_email">Email de Envío</Label>
                <Input
                  id="smtp_from_email"
                  value={localSettings.smtp_from_email || ''}
                  onChange={(e) => handleInputChange('smtp_from_email', e.target.value)}
                  placeholder="noreply@escuela.com"
                />
              </div>
              <div>
                <Label htmlFor="smtp_from_name">Nombre de Envío</Label>
                <Input
                  id="smtp_from_name"
                  value={localSettings.smtp_from_name || 'EduCore'}
                  onChange={(e) => handleInputChange('smtp_from_name', e.target.value)}
                  placeholder="EduCore"
                />
              </div>
            </div>

            <div className="flex gap-2 flex-wrap pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={testSmtpConnection}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="test-smtp-btn"
              >
                <Mail className="w-4 h-4 mr-1" />
                Probar SMTP (guardar y enviar correo de prueba)
              </Button>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="smtp-spam-tip">
              <p className="font-semibold text-amber-800 mb-1">¿Los correos caen en Spam?</p>
              Además de este SMTP, agrega los registros <strong>SPF, DKIM y DMARC</strong> en el DNS del dominio del correo remitente (lo hace el proveedor de tu dominio, ej. Google Workspace o tu hosting de Titan). Pide a soporte los registros exactos para tu proveedor si no los tienes configurados.
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="smtp_use_tls"
                checked={localSettings.smtp_use_tls !== false}
                onChange={(e) => handleInputChange('smtp_use_tls', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <Label htmlFor="smtp_use_tls" className="text-sm font-medium cursor-pointer">
                Usar TLS/SSL
              </Label>
            </div>

            <div className="flex items-center space-x-2 pt-2 border-t border-slate-100 mt-2">
              <input
                type="checkbox"
                id="calendar_reminders_enabled"
                data-testid="calendar-reminders-toggle"
                checked={localSettings.calendar_reminders_enabled !== false}
                onChange={(e) => handleInputChange('calendar_reminders_enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <Label htmlFor="calendar_reminders_enabled" className="text-sm font-medium cursor-pointer">
                Enviar recordatorio automático por correo a los padres el día antes de un evento del calendario (mañana y tarde)
              </Label>
            </div>

            <div className="flex gap-4">
              <Button onClick={testSmtpConnection} variant="outline">
                <Server className="w-4 h-4 mr-2" />
                Probar Conexión
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* School Configuration */}
      {activeSection === 'school' && (
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <School className="w-5 h-5 text-green-600" />
              Información de la Escuela
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label htmlFor="school_name">Nombre de la Escuela</Label>
                <Input
                  id="school_name"
                  value={localSettings.school_name || ''}
                  onChange={(e) => handleInputChange('school_name', e.target.value)}
                  placeholder="Nombre de la institución educativa"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="school_address">Dirección</Label>
                <Input
                  id="school_address"
                  value={localSettings.school_address || ''}
                  onChange={(e) => handleInputChange('school_address', e.target.value)}
                  placeholder="Dirección completa de la escuela"
                />
              </div>
              <div>
                <Label htmlFor="school_phone">Teléfono</Label>
                <Input
                  id="school_phone"
                  value={localSettings.school_phone || ''}
                  onChange={(e) => handleInputChange('school_phone', e.target.value)}
                  placeholder="(809) 555-0123"
                />
              </div>
              <div>
                <Label htmlFor="school_email">Email</Label>
                <Input
                  id="school_email"
                  type="email"
                  value={localSettings.school_email || ''}
                  onChange={(e) => handleInputChange('school_email', e.target.value)}
                  placeholder="info@escuela.edu.do"
                />
              </div>
            </div>

            {/* School Logo Upload */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Logo de la Escuela</h3>
              <div className="flex items-start gap-6">
                <div className="flex-1">
                  <Label htmlFor="school_logo">Subir Logo</Label>
                  <Input
                    id="school_logo"
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const response = await axios.post('/settings/upload-logo', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          
                          // Actualizar el estado local con la nueva URL del logo
                          handleInputChange('school_logo_url', response.data.logo_url);
                          
                          toast.success('Logo subido exitosamente');
                          
                          // Recargar configuración para obtener el nuevo logo
                          const settingsResponse = await axios.get('/settings');
                          setLocalSettings(settingsResponse.data);
                        } catch (error) {
                          console.error('Error uploading logo:', error);
                          toast.error('Error al subir el logo');
                        }
                      }
                    }}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos permitidos: PNG, JPG, WEBP. Este logo aparecerá en las boletas PDF.
                  </p>
                </div>
                {localSettings.school_logo_url && (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={localSettings.school_logo_url.startsWith('data:') ? localSettings.school_logo_url : `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/settings/logo`}
                        alt="Logo de la escuela"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<span class="text-gray-400 text-sm">Logo no disponible</span>';
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-600">Logo actual</p>
                  </div>
                )}
              </div>
            </div>

            {/* Grading System Configuration */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-green-600" />
                Sistema de Calificación
              </h3>
              <Alert className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Esta configuración se aplicará a todos los profesores de la escuela. Define cómo se registrarán y mostrarán las calificaciones.
                </AlertDescription>
              </Alert>
              
              <div>
                <Label htmlFor="grading_system">Tipo de Calificación</Label>
                <Select
                  value={localSettings.grading_system || 'points'}
                  onValueChange={(value) => handleInputChange('grading_system', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar sistema" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="points">Puntos (0-4)</SelectItem>
                    <SelectItem value="percentage">Porcentaje (0-100%)</SelectItem>
                    <SelectItem value="letter">Letras (A, B, C, D, F)</SelectItem>
                    <SelectItem value="custom_points">Puntos Personalizados (X/Y = %)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 mt-2">
                  {localSettings.grading_system === 'points' && '• Puntos: Escala de 0 a 4 (4.00 = A, 3.00 = B, 2.00 = C, etc.)'}
                  {localSettings.grading_system === 'percentage' && '• Porcentaje: Escala de 0% a 100% (90-100 = A, 80-89 = B, etc.)'}
                  {localSettings.grading_system === 'letter' && '• Letras: Calificación directa con A, B, C, D, F'}
                  {localSettings.grading_system === 'custom_points' && '• Puntos Personalizados: Ingresa puntos obtenidos sobre el máximo (ej: 19/20 = 95%). El denominador es configurable por evaluación.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Academic Configuration */}
      {activeSection === 'academic' && (
        <AcademicYearManager 
          promoteStudents={promoteStudents}
          gradeSequence={localSettings.grade_sequence}
        />
      )}

      {activeSection === 'academic-OLD' && (
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Configuración Académica
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Configure las fechas del año académico para la promoción automática de estudiantes.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="academic_year_start">Inicio del Año Académico</Label>
                <Input
                  id="academic_year_start"
                  type="date"
                  value={localSettings.academic_year_start ? 
                    new Date(localSettings.academic_year_start).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleInputChange('academic_year_start', 
                    e.target.value ? new Date(e.target.value).toISOString() : null)}
                />
              </div>
              <div>
                <Label htmlFor="academic_year_end">Fin del Año Académico</Label>
                <Input
                  id="academic_year_end"
                  type="date"
                  value={localSettings.academic_year_end ? 
                    new Date(localSettings.academic_year_end).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleInputChange('academic_year_end', 
                    e.target.value ? new Date(e.target.value).toISOString() : null)}
                />
              </div>
            </div>

            <div>
              <Label>Secuencia de Grados</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {(localSettings.grade_sequence || []).map((grade, index) => (
                  <Badge key={index} variant="secondary">
                    {index + 1}. {grade}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Los estudiantes serán promovidos automáticamente siguiendo esta secuencia al final del año académico.
              </p>
            </div>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-blue-900">Promoción Manual de Estudiantes</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Promover todos los estudiantes activos al siguiente grado ahora
                    </p>
                  </div>
                  <Button onClick={promoteStudents} className="bg-blue-600 hover:bg-blue-700">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Promover Estudiantes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      )}

      {/* Payment Configuration */}
      {activeSection === 'payments' && (
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-indigo-600" />
              Configuración de Pagos (Stripe)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Configure Stripe para procesar pagos automáticos. Los pagos se registrarán automáticamente en los perfiles de estudiantes.
              </AlertDescription>
            </Alert>

            <div className="space-y-6">
              <div>
                <Label htmlFor="stripe_publishable_key">Clave Pública de Stripe</Label>
                <Input
                  id="stripe_publishable_key"
                  value={localSettings.stripe_publishable_key || ''}
                  onChange={(e) => handleInputChange('stripe_publishable_key', e.target.value)}
                  placeholder="pk_test_..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Clave que se usa en el frontend (visible públicamente)
                </p>
              </div>
              <div>
                <Label htmlFor="stripe_secret_key">Clave Secreta de Stripe</Label>
                <Input
                  id="stripe_secret_key"
                  type="password"
                  value={localSettings.stripe_secret_key || ''}
                  onChange={(e) => handleInputChange('stripe_secret_key', e.target.value)}
                  placeholder="sk_test_..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Clave secreta para procesar pagos (mantén segura)
                </p>
              </div>
              <div>
                <Label htmlFor="stripe_webhook_secret">Webhook Secret</Label>
                <Input
                  id="stripe_webhook_secret"
                  type="password"
                  value={localSettings.stripe_webhook_secret || ''}
                  onChange={(e) => handleInputChange('stripe_webhook_secret', e.target.value)}
                  placeholder="whsec_..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Secret para verificar webhooks de Stripe
                </p>
              </div>
            </div>

            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Una vez configurado, los padres podrán realizar pagos directamente desde el portal. Los pagos se registrarán automáticamente usando el ID único del estudiante.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Location Configuration */}
      {activeSection === 'location' && (
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              Ubicación de la Escuela y Validación GPS
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Configure la ubicación de la escuela para validar que los empleados estén dentro del radio permitido al registrar ponches.
              </AlertDescription>
            </Alert>

            {/* Enable/Disable toggle */}
            <div className="flex items-center space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <input
                type="checkbox"
                id="clock_location_required"
                checked={localSettings.clock_location_required || false}
                onChange={(e) => handleInputChange('clock_location_required', e.target.checked)}
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                data-testid="clock-location-required"
              />
              <div>
                <Label htmlFor="clock_location_required" className="text-base font-semibold cursor-pointer">
                  Requerir ubicación para ponchar
                </Label>
                <p className="text-sm text-slate-500 mt-1">
                  Si está activo, los empleados deberán estar dentro del radio permitido para registrar entrada/salida
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="school_latitude">Latitud</Label>
                <Input
                  id="school_latitude"
                  type="number"
                  step="0.000001"
                  value={localSettings.school_latitude || ''}
                  onChange={(e) => handleInputChange('school_latitude', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="Ej: 18.4655"
                  data-testid="school-latitude"
                />
              </div>
              <div>
                <Label htmlFor="school_longitude">Longitud</Label>
                <Input
                  id="school_longitude"
                  type="number"
                  step="0.000001"
                  value={localSettings.school_longitude || ''}
                  onChange={(e) => handleInputChange('school_longitude', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="Ej: -69.9295"
                  data-testid="school-longitude"
                />
              </div>
              <div>
                <Label htmlFor="clock_radius_meters">Radio permitido (metros)</Label>
                <Input
                  id="clock_radius_meters"
                  type="number"
                  value={localSettings.clock_radius_meters || 200}
                  onChange={(e) => handleInputChange('clock_radius_meters', parseInt(e.target.value) || 200)}
                  placeholder="200"
                  data-testid="clock-radius"
                />
              </div>
            </div>

            {/* Get Current Location button */}
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  if (!navigator.geolocation) {
                    toast.error('Tu navegador no soporta geolocalización');
                    return;
                  }
                  toast.info('Obteniendo ubicación...');
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      handleInputChange('school_latitude', parseFloat(pos.coords.latitude.toFixed(6)));
                      handleInputChange('school_longitude', parseFloat(pos.coords.longitude.toFixed(6)));
                      toast.success('Ubicación obtenida exitosamente');
                    },
                    (err) => {
                      toast.error('Error al obtener ubicación: ' + err.message);
                    },
                    { enableHighAccuracy: true, timeout: 10000 }
                  );
                }}
                data-testid="get-location-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Usar mi ubicación actual
              </Button>
              <p className="text-sm text-slate-500">
                Presiona este botón estando en la escuela para capturar las coordenadas automáticamente
              </p>
            </div>

            {/* Preview */}
            {localSettings.school_latitude && localSettings.school_longitude && (
              <div className="bg-slate-50 rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Ubicación configurada
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Latitud:</span>
                    <span className="ml-2 font-mono font-medium">{localSettings.school_latitude}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Longitud:</span>
                    <span className="ml-2 font-mono font-medium">{localSettings.school_longitude}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Radio:</span>
                    <span className="ml-2 font-medium">{localSettings.clock_radius_meters || 200} metros</span>
                  </div>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${localSettings.school_latitude},${localSettings.school_longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm mt-2 inline-block"
                >
                  Ver en Google Maps
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <Button 
          onClick={() => setLocalSettings(settings)} 
          variant="outline"
          disabled={settingsLoading}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Descartar Cambios
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={settingsLoading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Save className="w-4 h-4 mr-2" />
          {settingsLoading ? 'Guardando...' : 'Guardar Configuraciones'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsTab;